/**
 * A consulta das prévias de link (27/10/2026): o cartão do site (título, descrição, capa) que o
 * servidor busca e devolve para os links de um texto.
 *
 * Duas portas, o mesmo contrato: `POST /previas-de-link/consultar` (colaborador, sessão interna)
 * e `POST /portal/previas-de-link/consultar` (parceiro, sessão do portal). Cada uma usa o cliente
 * axios da sua sessão, que já injeta o token certo: `api` (`seweb_auth`) e `portalApi`
 * (`portal_access_token`). Misturar os dois é 401 e, no `api`, derruba a sessão interna.
 *
 * Caminho SEM `/api/v1`: o `baseURL` dos dois clientes já termina em `/api/v1` (`VITE_API_URL`);
 * um segundo prefixo aqui duplicaria e o backend responderia 404 — cicatriz travada por
 * `previasDeLinkApi.url.test.ts`.
 *
 * O servidor leva até ~5 s no site + ~5 s na imagem; o `api` tem `timeout` de 10 s e o `portalApi`
 * de 15 s, curtos para isso. Por isso o pedido leva o próprio `timeout`. Quem chama trata a falha
 * como "sem cartão", sem toast: o link continua clicável.
 */
import api from "@/services/api";
import { portalApi } from "@/services/portalApi";

/** De que sessão vem a consulta: o colaborador (`interno`) ou o parceiro (`portal`). */
export type OrigemDasPrevias = "interno" | "portal";

/** O cartão do site, como o servidor devolve. `imagem_*` vêm nulos juntos quando não há capa. */
export interface CartaoDoSite {
  titulo: string;
  descricao: string | null;
  imagem_url: string | null;
  imagem_largura: number | null;
  imagem_altura: number | null;
  dominio: string;
}

/** Um link da consulta; `link` volta exatamente como foi mandado. `cartao: null` = sem cartão. */
export interface PreviaConsultada {
  link: string;
  cartao: CartaoDoSite | null;
}

/** `ligada: false` = o interruptor da empresa está desligado (todos os cartões vêm nulos). */
export interface RespostaDePrevias {
  ligada: boolean;
  previas: PreviaConsultada[];
}

/** Dobro do que o `api` espera por padrão: cobre o site lento + a imagem lenta do servidor. */
export const TEMPO_LIMITE_DA_CONSULTA_MS = 20_000;

/** Consulta o cartão de cada link (1 a 20 por pedido, cada um até 2048 caracteres). */
export async function consultarPrevias(
  links: string[],
  origem: OrigemDasPrevias
): Promise<RespostaDePrevias> {
  const corpo = { links };
  const opcoes = { timeout: TEMPO_LIMITE_DA_CONSULTA_MS };
  const resposta =
    origem === "portal"
      ? await portalApi.post<RespostaDePrevias>("/portal/previas-de-link/consultar", corpo, opcoes)
      : await api.post<RespostaDePrevias>("/previas-de-link/consultar", corpo, opcoes);
  return resposta.data;
}
